/**
 * Realtime Subscription Pool - Manages Supabase realtime subscriptions
 * to reduce connections and improve efficiency
 */

import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { dbLogger } from './structuredLogger';
/**
 * Payload type for Supabase realtime postgres_changes events
 */
export interface RealtimePayload {
  commit_timestamp: string;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: Record<string, unknown>;
  old: Record<string, unknown>;
  schema: string;
  table: string;
  errors: string[] | null;
}

interface SubscriptionConfig {
  schema: string;
  table: string;
  filter?: string;
  event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
}

interface SubscriptionHandler {
  id: string;
  callback: (payload: RealtimePayload) => void;
  config: SubscriptionConfig;
  priority: 'low' | 'normal' | 'high';
}

interface PooledChannel {
  channel: RealtimeChannel;
  handlers: Map<string, SubscriptionHandler>;
  config: SubscriptionConfig;
  lastActivity: number;
  subscribeCount: number;
}

class RealtimeSubscriptionPool {
  private channels = new Map<string, PooledChannel>();
  private handlerIndex = new Map<string, string>(); // handlerId -> channelKey
  private stats = {
    totalSubscriptions: 0,
    activeChannels: 0,
    pooledSubscriptions: 0,
    duplicatesPrevented: 0
  };

  private readonly CHANNEL_TIMEOUT = 300000; // 5 minutes
  private readonly MAX_HANDLERS_PER_CHANNEL = 50;
  
  // Backpressure handling
  private updateQueue: Array<{ handlerId: string; payload: RealtimePayload }> = [];
  private isProcessingQueue = false;
  private readonly MAX_UPDATES_PER_SECOND = 3;
  private readonly QUEUE_PROCESS_INTERVAL = 333; // ~3 updates/sec

  /**
   * Subscribe to realtime changes with pooling
   */
  subscribe(
    config: SubscriptionConfig,
    callback: (payload: RealtimePayload) => void,
    options: {
      priority?: 'low' | 'normal' | 'high';
      debounceMs?: number;
    } = {}
  ): string {
    const { priority = 'normal', debounceMs = 0 } = options;
    const handlerId = this.generateHandlerId();
    const channelKey = this.createChannelKey(config);

    this.stats.totalSubscriptions++;

    // Apply debouncing if requested
    const finalCallback = debounceMs > 0 
      ? this.debounce(callback, debounceMs)
      : callback;

    // Check for existing channel
    let pooledChannel = this.channels.get(channelKey);

    if (pooledChannel) {
      // Reuse existing channel
      this.stats.duplicatesPrevented++;
      this.addHandlerToChannel(pooledChannel, handlerId, config, finalCallback, priority);
      
      dbLogger.debug('Subscription pooled to existing channel', {
        channelKey,
        handlerId,
        totalHandlers: pooledChannel.handlers.size
      });
    } else {
      // Create new channel
      pooledChannel = this.createNewChannel(channelKey, config);
      this.addHandlerToChannel(pooledChannel, handlerId, config, finalCallback, priority);
      this.stats.activeChannels++;
      
      dbLogger.debug('New realtime channel created', {
        channelKey,
        handlerId,
        config
      });
    }

    this.handlerIndex.set(handlerId, channelKey);
    this.stats.pooledSubscriptions++;

    // Cleanup old channels periodically
    this.scheduleCleanup();

    return handlerId;
  }

  /**
   * Unsubscribe from realtime changes
   */
  unsubscribe(handlerId: string): boolean {
    const channelKey = this.handlerIndex.get(handlerId);
    if (!channelKey) {
      dbLogger.warn('Handler not found for unsubscribe', { handlerId });
      return false;
    }

    const pooledChannel = this.channels.get(channelKey);
    if (!pooledChannel) {
      dbLogger.warn('Channel not found for unsubscribe', { channelKey, handlerId });
      return false;
    }

    // Remove handler
    pooledChannel.handlers.delete(handlerId);
    this.handlerIndex.delete(handlerId);
    this.stats.pooledSubscriptions--;

    dbLogger.debug('Handler unsubscribed', {
      handlerId,
      channelKey,
      remainingHandlers: pooledChannel.handlers.size
    });

    // Remove channel if no handlers left
    if (pooledChannel.handlers.size === 0) {
      this.removeChannel(channelKey);
    }

    return true;
  }

  /**
   * Unsubscribe all handlers for a specific table/project
   */
  unsubscribeByPattern(pattern: string | RegExp): number {
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    const handlersToRemove: string[] = [];

    for (const [handlerId, channelKey] of this.handlerIndex) {
      if (regex.test(channelKey)) {
        handlersToRemove.push(handlerId);
      }
    }

    handlersToRemove.forEach(handlerId => this.unsubscribe(handlerId));

    dbLogger.debug('Bulk unsubscribe completed', {
      pattern: pattern.toString(),
      handlersRemoved: handlersToRemove.length
    });

    return handlersToRemove.length;
  }

  private createNewChannel(channelKey: string, config: SubscriptionConfig): PooledChannel {
    const channel = supabase.channel(channelKey);
    
    const pooledChannel: PooledChannel = {
      channel,
      handlers: new Map(),
      config,
      lastActivity: Date.now(),
      subscribeCount: 0
    };

    // Set up the actual Supabase subscription with backpressure
    channel.on(
      'postgres_changes' as any,
      {
        event: config.event,
        schema: config.schema,
        table: config.table,
        filter: config.filter
      } as any,
      (payload) => {
        pooledChannel.lastActivity = Date.now();
        
        // Enqueue updates for backpressure handling
        for (const [handlerId] of pooledChannel.handlers) {
          this.enqueueUpdate(handlerId, payload);
        }
      }
    );

    // Subscribe to the channel
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        pooledChannel.subscribeCount++;
        dbLogger.debug('Channel subscribed successfully', { channelKey, status });
      } else if (status === 'CHANNEL_ERROR') {
        dbLogger.error('Channel subscription error', { channelKey, status });
      } else {
        dbLogger.debug('Channel status', { channelKey, status });
      }
    });

    this.channels.set(channelKey, pooledChannel);
    return pooledChannel;
  }

  private addHandlerToChannel(
    pooledChannel: PooledChannel,
    handlerId: string,
    config: SubscriptionConfig,
    callback: (payload: RealtimePayload) => void,
    priority: 'low' | 'normal' | 'high'
  ): void {
    if (pooledChannel.handlers.size >= this.MAX_HANDLERS_PER_CHANNEL) {
      throw new Error(`Channel has reached maximum handlers limit: ${this.MAX_HANDLERS_PER_CHANNEL}`);
    }

    const handler: SubscriptionHandler = {
      id: handlerId,
      callback,
      config,
      priority
    };

    pooledChannel.handlers.set(handlerId, handler);
  }

  private removeChannel(channelKey: string): void {
    const pooledChannel = this.channels.get(channelKey);
    if (!pooledChannel) return;

    // Unsubscribe from Supabase
    supabase.removeChannel(pooledChannel.channel);
    
    // Remove from our tracking
    this.channels.delete(channelKey);
    this.stats.activeChannels--;

    dbLogger.debug('Channel removed', { channelKey });
  }

  private createChannelKey(config: SubscriptionConfig): string {
    const parts = [config.schema, config.table, config.event];
    if (config.filter) {
      parts.push(config.filter);
    }
    return parts.join(':');
  }

  private generateHandlerId(): string {
    return `handler_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private debounce<T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
  ): T {
    let timeout: ReturnType<typeof setTimeout>;
    return ((...args: unknown[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    }) as T;
  }

  private scheduleCleanup(): void {
    // Use setTimeout to avoid blocking
    setTimeout(() => this.cleanupInactiveChannels(), 0);
  }

  private cleanupInactiveChannels(): void {
    const now = Date.now();
    const channelsToRemove: string[] = [];

    for (const [channelKey, pooledChannel] of this.channels) {
      const inactiveTime = now - pooledChannel.lastActivity;
      
      if (inactiveTime > this.CHANNEL_TIMEOUT && pooledChannel.handlers.size === 0) {
        channelsToRemove.push(channelKey);
      }
    }

    channelsToRemove.forEach(channelKey => this.removeChannel(channelKey));

    if (channelsToRemove.length > 0) {
      dbLogger.info('Inactive channels cleaned up', {
        channelsRemoved: channelsToRemove.length,
        activeChannels: this.channels.size
      });
    }
  }

  /**
   * Get subscription pool statistics
   */
  getStats() {
    const channelDetails = Array.from(this.channels.entries()).map(([key, channel]) => ({
      key,
      handlers: channel.handlers.size,
      lastActivity: channel.lastActivity,
      subscribeCount: channel.subscribeCount
    }));

    return {
      ...this.stats,
      channelDetails,
      efficiency: this.stats.totalSubscriptions > 0 
        ? Math.round((this.stats.duplicatesPrevented / this.stats.totalSubscriptions) * 100)
        : 0
    };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      totalSubscriptions: 0,
      activeChannels: this.channels.size,
      pooledSubscriptions: 0,
      duplicatesPrevented: 0
    };
  }

  /**
   * Force cleanup all channels (useful for testing or reset)
   */
  cleanup(): void {
    for (const [channelKey] of this.channels) {
      this.removeChannel(channelKey);
    }
    this.handlerIndex.clear();
    this.resetStats();
    
    dbLogger.info('All channels cleaned up');
  }

  /**
   * Enqueue update for backpressure handling
   */
  private enqueueUpdate(handlerId: string, payload: RealtimePayload): void {
    this.updateQueue.push({ handlerId, payload });
    
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  /**
   * Process update queue with rate limiting
   */
  private async processQueue(): Promise<void> {
    if (this.updateQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    this.isProcessingQueue = true;
    const batchSize = Math.min(this.MAX_UPDATES_PER_SECOND, this.updateQueue.length);
    const batch = this.updateQueue.splice(0, batchSize);

    // Process batch
    for (const { handlerId, payload } of batch) {
      const channelKey = this.handlerIndex.get(handlerId);
      if (!channelKey) continue;

      const pooledChannel = this.channels.get(channelKey);
      if (!pooledChannel) continue;

      const handler = pooledChannel.handlers.get(handlerId);
      if (handler) {
        try {
          handler.callback(payload);
        } catch (error) {
          dbLogger.error('Handler callback error', {
            handlerId,
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }
    }

    // Schedule next batch
    setTimeout(() => this.processQueue(), this.QUEUE_PROCESS_INTERVAL);
  }
}

// Global instance
export const realtimeSubscriptionPool = new RealtimeSubscriptionPool();

// Utility function for easy subscription
export const subscribeToTable = (
  table: string,
  callback: (payload: RealtimePayload) => void,
  options: {
    event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
    filter?: string;
    priority?: 'low' | 'normal' | 'high';
    debounceMs?: number;
  } = {}
): string => {
  const { event = '*', filter, ...rest } = options;
  
  return realtimeSubscriptionPool.subscribe(
    {
      schema: 'public',
      table,
      event,
      filter
    },
    callback,
    rest
  );
};